import type { ReactNode } from 'react';
import { createContext, useContext, useEffect, useState } from 'react';

import useShortlist from '@rover/components/src/shortlist/hooks/useShortlist';
import type { EnhancedPersonSitterProfile } from '@rover/rsdk/src/apiClient/latest/model/enhancedPersonSitterProfile';

const ContactListLatchContext = createContext(false);

type ProviderProps = {
  sitterSlug: string;
  children: ReactNode;
};

/**
 * Records once per profile whether the owner has had sitters saved, so every
 * ContactButton on the page agrees, including one in a sheet that mounts later.
 */
export const ContactListLatchProvider = ({ sitterSlug, children }: ProviderProps): JSX.Element => {
  const { count } = useShortlist();
  // Keyed by slug so the latch resets when the page moves to another profile.
  const [latchedSlug, setLatchedSlug] = useState<string | null>(null);
  useEffect(() => {
    if (count > 0) {
      setLatchedSlug(sitterSlug);
    }
  }, [count, sitterSlug]);

  return (
    <ContactListLatchContext.Provider value={latchedSlug === sitterSlug}>
      {children}
    </ContactListLatchContext.Provider>
  );
};

export default function useContactButtonMode(
  sitter: EnhancedPersonSitterProfile | undefined
): 'shortlist' | 'contact' {
  const { isVisible, count } = useShortlist();
  // An owner who arrives with sitters saved keeps the contact list for the rest of
  // the page view, even after emptying it. Deciding again mid-view would swap the
  // contact list's buttons for a different control, dropping keyboard focus to the
  // document body when the list closes.
  const hasSavedSitters = useContext(ContactListLatchContext);
  const hasBookedBefore = sitter?.authenticatedContext?.hasBookedBefore ?? false;

  if (!isVisible) return 'contact';
  // An owner returning to a sitter they have already booked, with nobody else on
  // their contact list, has nothing to compare, so send them to the conversation
  // instead of the list. The server and the hydrating client both see an empty
  // list, so both render the plain button and it never flickers in.
  if (hasBookedBefore && count === 0 && !hasSavedSitters) return 'contact';
  return 'shortlist';
}
